/**
 * Individual Observations row identity is the safety episode.
 * Parent severity and parent workflow status stay labeled as parent fields.
 * Evidence is included only when incident_evidence.episode_id is this episode.
 */

export type ObservationEvidenceRow = {
  episode_id?: string | null;
  incident_id?: string | null;
  kind?: string | null;
  status?: string | null;
  filename?: string | null;
  id?: string | null;
  evidence_id?: string | null;
};

export const NO_PARENT_INCIDENT_LABEL = 'No parent incident';

function text(value: unknown): string {
  return String(value ?? '').trim();
}

const PARENT_WORKFLOW_STATUSES = new Set(['OPEN', 'ACKED', 'IN_REVIEW', 'CLOSED', 'SUPPRESSED']);

/**
 * Parent workflow status only. A missing parent is "No parent incident".
 * Episode state, including cleared, stays off that label.
 */
export function observationParentStatusPresentation(row: {
  parent_incident_status?: string | null;
  status?: string | null;
  episode_status?: string | null;
} | null | undefined): {
  parentLabel: string;
  parentStatus: string | null;
  episodeState: string | null;
} {
  const parent = text(row?.parent_incident_status).toUpperCase();
  const episodeState = text(row?.episode_status) || null;
  if (!parent || !PARENT_WORKFLOW_STATUSES.has(parent)) {
    return {
      parentLabel: NO_PARENT_INCIDENT_LABEL,
      parentStatus: null,
      episodeState,
    };
  }
  return {
    parentLabel: parent,
    parentStatus: parent,
    episodeState,
  };
}

export function applyObservationEpisodeSearchParam(
  params: URLSearchParams,
  episodeId: string | null | undefined,
): void {
  const id = text(episodeId);
  if (id) params.set('episode_id', id);
  else params.delete('episode_id');
}

export function observationReviewIncidentId(row: {
  linked_detailed_record_id?: string | null;
  incident_id?: string | null;
} | null | undefined): string {
  return text(row?.linked_detailed_record_id || row?.incident_id);
}

export function observationReviewOpenOptions(
  row: { group_key?: string | null; episode_id?: string | null } | null | undefined,
  tableMode: string,
): { exact: boolean; workflowId: string | null; episodeId: string | null } {
  const groupedRow = Boolean(text(row?.group_key)) || tableMode === 'grouped';
  const episodeId = groupedRow ? '' : text(row?.episode_id);
  return {
    exact: !groupedRow,
    workflowId: groupedRow ? text(row?.group_key) || null : null,
    episodeId: episodeId || null,
  };
}

export function evidenceRowsForEpisode(
  rows: ObservationEvidenceRow[] | null | undefined,
  episodeId: string,
): ObservationEvidenceRow[] {
  const wanted = text(episodeId);
  if (!wanted) return [];
  return (Array.isArray(rows) ? rows : []).filter((row) => text(row?.episode_id) === wanted);
}

function evidenceKindStatus(
  rows: ObservationEvidenceRow[],
  kind: 'SNAPSHOT' | 'CLIP',
): string | null {
  const match = rows.find((row) => text(row.kind).toUpperCase() === kind);
  const status = text(match?.status).toUpperCase();
  return status || null;
}

export function mapSafetyEpisodeObservation(item: any): Record<string, any> {
  const episodeId = text(item?.episode_id);
  const qualifiedAt = text(item?.qualified_at);
  const parentStatus = text(item?.parent_incident_status).toUpperCase();
  const ownEvidence = evidenceRowsForEpisode(item?.evidence, episodeId);
  const attachments = ownEvidence.map((row) => ({
    episode_id: episodeId,
    incident_id: row.incident_id || item?.incident_id || null,
    kind: row.kind || null,
    status: row.status || null,
    filename: row.filename || null,
    id: row.id || row.evidence_id || null,
  }));
  const workflowStatus = PARENT_WORKFLOW_STATUSES.has(parentStatus) ? parentStatus : null;

  return {
    id: episodeId,
    episode_id: episodeId,
    observation_episode_id: episodeId,
    incident_id: text(item?.incident_id),
    client_incident_id: text(item?.client_incident_id) || null,
    title: text(item?.incident_type) || episodeId || 'Observation',
    description: '',
    severity: text(item?.parent_severity),
    parent_severity: text(item?.parent_severity) || null,
    status: workflowStatus,
    parent_incident_status: parentStatus || null,
    episode_status: text(item?.episode_status) || null,
    qualified_at: qualifiedAt || null,
    created_at: qualifiedAt,
    updated_at: qualifiedAt,
    last_seen_at: qualifiedAt || null,
    last_seen: qualifiedAt || null,
    zone_id: item?.zone_id || undefined,
    device_id: item?.device_id || undefined,
    camera_id: item?.camera_id || undefined,
    incident_type: item?.incident_type || undefined,
    family: item?.family || undefined,
    org_id: item?.org_id || undefined,
    site_id: item?.site_id || undefined,
    evidence_status: text(item?.evidence_status) || null,
    terminal_reason: text(item?.terminal_reason) || null,
    snapshot_id: item?.snapshot_id || null,
    clip_id: item?.clip_id || null,
    snapshot_status: evidenceKindStatus(attachments, 'SNAPSHOT'),
    clip_status: evidenceKindStatus(attachments, 'CLIP'),
    snapshot_uri: null,
    clip_uri: null,
    evidence: { attachments },
    review_media: undefined,
    evidence_selection: null,
    version: Number.isFinite(Number(item?.revision)) ? Number(item.revision) : 1,
    assigned_to: null,
    raw: item,
  };
}

export function scopeIncidentToEpisodeEvidence<T extends Record<string, any>>(
  incident: T,
  episodeId: string,
  evidenceRows: ObservationEvidenceRow[] | null | undefined,
): T & { observation_episode_id: string; episode_id: string } {
  const wanted = text(episodeId);
  const own = evidenceRowsForEpisode(evidenceRows, wanted).map((row) => ({
    ...row,
    episode_id: wanted,
  }));
  return {
    ...incident,
    observation_episode_id: wanted,
    episode_id: wanted,
    snapshot_uri: null,
    clip_uri: null,
    snapshot_status: evidenceKindStatus(own, 'SNAPSHOT'),
    clip_status: evidenceKindStatus(own, 'CLIP'),
    review_media: undefined,
    evidence_selection: null,
    evidence: { attachments: own },
  } as T & { observation_episode_id: string; episode_id: string };
}
